// ==========================================
// src/pages/PainelMotorista.jsx
// PAINEL DO MOTORISTA - VERSÃO DEFINITIVA CORRIGIDA
// ✅ Status 'aceito'/'pago'/'em_execucao'/'concluido' (compatível com CHECK)
// ✅ motorista_id usa auth.uid() consistentemente
// ✅ handleConcluirEntrega corrige query e atualiza Supabase + localStorage
// ✅ FIX: Usa numero_contrato para contratos do localStorage (CTR-...)
// ✅ FIX: Usa id (UUID) para contratos do Supabase
// ==========================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { supabase } from '@/lib/supabase';
import { 
  Truck, MapPin, CheckCircle, Clock, Star,
  Package, Search, ChevronRight, Loader2,
  ArrowRight, Award, Calendar,
  Weight, Navigation, Phone, FileText,
  TrendingUp, User, LogOut, Bell,
  DollarSign, Shield, Zap, Heart,
  Crown, Trophy, Target, Flame, Medal, Sparkles,
  Gem, Diamond, Route, Gift
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useNivelMotorista, NIVEIS_MOTORISTA, SELOS_MOTORISTA } from '@/lib/nivelMotorista';
import { registrarMotorista } from '@/lib/onesignal';

const ICONES_SELO = {
  Truck, Clock, Shield, Heart, Star, Crown, Zap, Trophy, Award, Gem, Diamond, Flame, Target
};

// ✅ FUNÇÕES AUXILIARES PARA LOCALSTORAGE
const buscarContratosLocalStorage = (userId, userEmail) => {
  try {
    const contratos = JSON.parse(localStorage.getItem('borafrete_contratos') || '[]');
    return contratos.filter(c => 
      (userId && c.motorista_id === userId) || 
      (userEmail && c.motorista_email === userEmail)
    );
  } catch {
    return [];
  }
};

const buscarCargaLocalStorage = (cargaId) => {
  try {
    const cargas = JSON.parse(localStorage.getItem('borafrete_cargas') || '[]');
    return cargas.find(c => c.id === cargaId);
  } catch {
    return null;
  }
};

// ✅ BUSCA CARGA DO SUPABASE — SEM COLUNA "valor" (não existe) + .maybeSingle()
const buscarCargaSupabase = async (cargaId) => {
  if (!cargaId) return null;
  try {
    const { data, error } = await supabase
      .from('cargas')
      .select('id, titulo, origem_cidade, origem_estado, destino_cidade, destino_estado, valor_frete, peso, tipo_veiculo, empresa_nome, empresa_telefone, empresa_email')
      .eq('id', cargaId)
      .maybeSingle();

    if (error) {
      console.warn('[PainelMotorista] Erro buscar carga Supabase:', error.message);
      return null;
    }
    return data;
  } catch (err) {
    console.warn('[PainelMotorista] Erro query carga:', err);
    return null;
  }
};

const PainelMotorista = () => {
  const navigate = useNavigate();
  const { user: authUser, isAuthenticated, logout } = useAuth();

  const [user, setUser] = useState(null);
  const [cargaAtual, setCargaAtual] = useState(null);
  const [historicoCargas, setHistoricoCargas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [concluindo, setConcluindo] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState('atual');
  const [notificacoesNaoLidas, setNotificacoesNaoLidas] = useState(0);

  // ✅ EVITA LOOP INFINITO
  const dadosCarregados = useRef(false);
  const userCarregado = useRef(false);

  const { 
    entregas, 
    nivel, 
    progresso, 
    proximoNivel, 
    entregasRestantes,
    verificarEvolucao,
    selos,
    stats,
    pontuacaoTotal,
    loading: loadingNivel
  } = useNivelMotorista(user);

  // Proteção contra undefined
  const selosSeguros = Array.isArray(selos) ? selos : [];
  const statsSeguros = stats || {};
  const pontuacaoSegura = typeof pontuacaoTotal === 'number' ? pontuacaoTotal : 0;
  const historicoSeguro = Array.isArray(historicoCargas) ? historicoCargas : [];
  const niveisSeguros = Array.isArray(NIVEIS_MOTORISTA) ? NIVEIS_MOTORISTA : [];
  const selosBaseSeguros = Array.isArray(SELOS_MOTORISTA) ? SELOS_MOTORISTA : [];

  // 🔥 CARREGAR USUÁRIO — AUTH USER ID PREVALECE
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const carregarUsuario = async () => {
      if (userCarregado.current) return;

      let userData = authUser;

      if (!userData?.id) {
        console.log('[PainelMotorista] AuthContext vazio, buscando Supabase direto...');
        try {
          const { data: { user: sbUser }, error: sbError } = await supabase.auth.getUser();
          if (sbUser && !sbError) {
            userData = {
              id: sbUser.id,
              email: sbUser.email,
              nome: sbUser.user_metadata?.nome || sbUser.email?.split('@')[0] || 'Motorista'
            };
            console.log('[PainelMotorista] User do Supabase:', userData);
          }
        } catch (e) {
          console.warn('[PainelMotorista] Erro getUser Supabase:', e);
        }
      }

      // Fallback localStorage
      if (!userData?.id) {
        const stored = localStorage.getItem('borafrete_user');
        if (stored) {
          try {
            userData = JSON.parse(stored);
            console.log('[PainelMotorista] User do localStorage:', userData);
          } catch (e) {
            console.error('Erro ao parsear user:', e);
          }
        }
      }

      if (!userData?.id) {
        console.error('[PainelMotorista] Nenhum user encontrado!');
        navigate('/login');
        return;
      }

      const userFinal = {
        ...userData,
        id: authUser?.id || userData.id,
        email: authUser?.email || userData.email
      };

      localStorage.setItem('borafrete_user', JSON.stringify(userFinal));

      try {
        const { data, error } = await supabase
          .from('motoristas')
          .select('*')
          .eq('usuario_id', userFinal.id)
          .single();

        if (data) {
          const userCompleto = { 
            ...userFinal, 
            ...data, 
            nome: data.nome_completo || userFinal.nome || userFinal.email?.split('@')[0],
            // ✅ Guarda o ID da tabela motoristas também para referência
            motorista_db_id: data.id
          };
          setUser(userCompleto);
          localStorage.setItem('borafrete_user', JSON.stringify(userCompleto));
          console.log('[PainelMotorista] User completo:', userCompleto);
        } else {
          setUser(userFinal);
        }
      } catch (err) {
        console.warn('[PainelMotorista] Erro buscar motorista:', err);
        setUser(userFinal);
      }

      if (userFinal?.email) {
        registrarMotorista(userFinal.email, userFinal.estado);
      }

      userCarregado.current = true;
    };

    carregarUsuario();
  }, [isAuthenticated, authUser, navigate]);

  // 🔥 CARREGAR DADOS — USA ID REAL DO AUTH
  const carregarDados = useCallback(async (userData) => {
    const userIdReal = userData?.id;

    if (!userIdReal) {
      console.error('[PainelMotorista] Sem ID do usuário!');
      return;
    }

    if (dadosCarregados.current) return;

    try {
      setLoading(true);
      console.log('[PainelMotorista] Carregando dados para ID:', userIdReal);

      let contratosLista = [];

      // 1️⃣ Busca Supabase por ID real do auth (motorista_id = auth.uid)
      try {
        const { data: contratos, error } = await supabase
          .from('contratos')
          .select('*')
          .eq('motorista_id', userIdReal)
          .order('created_at', { ascending: false });

        if (!error && Array.isArray(contratos)) {
          contratosLista = contratos;
          console.log('[PainelMotorista] Contratos Supabase:', contratos.length);
        } else if (error) {
          console.warn('[PainelMotorista] Erro Supabase:', error.message);
        }
      } catch (e) {
        console.warn('[PainelMotorista] Erro query:', e);
      }

      // 2️⃣ Fallback localStorage
      if (contratosLista.length === 0) {
        contratosLista = buscarContratosLocalStorage(userIdReal, userData.email);
        console.log('[PainelMotorista] Contratos localStorage:', contratosLista.length);
      }

      // ✅ SINCRONIZA contratos no localStorage
      if (contratosLista.length > 0) {
        const todosContratos = JSON.parse(localStorage.getItem('borafrete_contratos') || '[]');
        const novosContratos = [...todosContratos];

        contratosLista.forEach(c => {
          const idx = novosContratos.findIndex(nc => nc.id === c.id);
          if (idx >= 0) novosContratos[idx] = c;
          else novosContratos.push(c);
        });

        localStorage.setItem('borafrete_contratos', JSON.stringify(novosContratos));
      }

      // ✅ STATUS ATIVOS CORRIGIDOS — só os que existem no CHECK constraint
      const statusAtivos = ['aceito', 'pago', 'em_execucao'];

      const contratoAtivo = Array.isArray(contratosLista) 
        ? contratosLista.find(c => statusAtivos.includes(c.status?.toLowerCase()))
        : null;

      if (contratoAtivo) {
        let cargaData = null;
        if (contratoAtivo.carga_id) {
          cargaData = await buscarCargaSupabase(contratoAtivo.carga_id);
          if (!cargaData) {
            cargaData = buscarCargaLocalStorage(contratoAtivo.carga_id);
          }
        }

        // ✅ FIX: Guarda o numero_contrato separadamente do id
        const isLocalStorageContract = contratoAtivo.id && contratoAtivo.id.toString().startsWith('CTR-');
        
        setCargaAtual({
          // Se for contrato do localStorage (CTR-...), guarda o UUID real em contrato_id
          // Se for do Supabase, o id já é o UUID
          id: isLocalStorageContract ? null : contratoAtivo.id,
          contrato_id: isLocalStorageContract ? null : contratoAtivo.id,
          numero_contrato: contratoAtivo.numero_contrato || contratoAtivo.id,
          carga_id: contratoAtivo.carga_id || cargaData?.id,
          titulo: cargaData?.titulo || contratoAtivo.produto || 'Carga sem título',
          origem_cidade: cargaData?.origem_cidade || contratoAtivo.origem_cidade || '-',
          origem_estado: cargaData?.origem_estado || contratoAtivo.origem_estado || '-',
          destino_cidade: cargaData?.destino_cidade || contratoAtivo.destino_cidade || '-',
          destino_estado: cargaData?.destino_estado || contratoAtivo.destino_estado || '-',
          valor: cargaData?.valor_frete || contratoAtivo.valor_frete || contratoAtivo.valor || 0,
          peso: cargaData?.peso || contratoAtivo.peso || '-',
          tipo_veiculo: cargaData?.tipo_veiculo || contratoAtivo.tipo_veiculo || '-',
          empresa_nome: cargaData?.empresa_nome || contratoAtivo.empresa_nome || 'Empresa',
          empresa_telefone: cargaData?.empresa_telefone || contratoAtivo.empresa_telefone,
          empresa_email: cargaData?.empresa_email || contratoAtivo.empresa_email,
          status: contratoAtivo.status,
          data_inicio: contratoAtivo.data_inicio || contratoAtivo.data_aceite,
          data_fim: contratoAtivo.data_fim || contratoAtivo.data_vencimento,
          progresso: calcularProgresso(contratoAtivo.data_inicio, contratoAtivo.data_fim),
          taxa_agenciamento: contratoAtivo.taxa_agenciamento || 50,
          // Flag para saber se é localStorage (sem UUID no Supabase)
          isLocalOnly: isLocalStorageContract
        });
      } else {
        setCargaAtual(null);
      }

      // Histórico - concluídas
      const concluidas = Array.isArray(contratosLista)
        ? contratosLista
            .filter(c => c.status?.toLowerCase() === 'concluido')
            .sort((a, b) => {
              const dateA = new Date(b.data_fim || b.created_at || b.data_inicio || 0);
              const dateB = new Date(a.data_fim || a.created_at || a.data_inicio || 0);
              return dateA - dateB;
            })
        : [];

      const historicoComCargas = await Promise.all(
        concluidas.map(async (c) => {
          let cargaInfo = null;
          if (c.carga_id) {
            cargaInfo = await buscarCargaSupabase(c.carga_id);
            if (!cargaInfo) {
              cargaInfo = buscarCargaLocalStorage(c.carga_id);
            }
          }

          return {
            id: c.id,
            carga_id: c.carga_id,
            titulo: cargaInfo?.titulo || c.produto || 'Carga',
            origem_cidade: cargaInfo?.origem_cidade || c.origem_cidade || '-',
            origem_estado: cargaInfo?.origem_estado || c.origem_estado || '-',
            destino_cidade: cargaInfo?.destino_cidade || c.destino_cidade || '-',
            destino_estado: cargaInfo?.destino_estado || c.destino_estado || '-',
            valor: cargaInfo?.valor_frete || c.valor_frete || c.valor || 0,
            peso: cargaInfo?.peso || c.peso,
            empresa_nome: cargaInfo?.empresa_nome || c.empresa_nome,
            data_conclusao: c.data_fim || c.created_at || c.data_inicio,
            status: c.status,
            data_inicio: c.data_inicio || c.data_aceite
          };
        })
      );

      setHistoricoCargas(historicoComCargas);
      dadosCarregados.current = true;

      console.log('[PainelMotorista] Dados carregados:', {
        contratos: contratosLista.length,
        ativo: contratoAtivo?.id,
        historico: historicoComCargas.length
      });

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 🔥 DISPARA CARREGAMENTO
  useEffect(() => {
    if (authUser?.id) {
      console.log('[PainelMotorista] Disparando carregarDados com authUser:', authUser.id);
      carregarDados(authUser);
    } else if (user?.id) {
      console.log('[PainelMotorista] Disparando carregarDados com user:', user.id);
      carregarDados(user);
    }
  }, [authUser, user, carregarDados]);

  const calcularProgresso = (dataInicio, dataFim) => {
    if (!dataInicio || !dataFim) return 0;
    const inicio = new Date(dataInicio).getTime();
    const fim = new Date(dataFim).getTime();
    const agora = Date.now();

    if (agora >= fim) return 100;
    if (agora <= inicio) return 0;

    return Math.round(((agora - inicio) / (fim - inicio)) * 100);
  };

  // ============================================================
  // ✅ HANDLE CONCLUIR ENTREGA — CORRIGIDO E COMPLETO
  // ✅ FIX: Usa numero_contrato para buscar contratos do localStorage
  // ✅ FIX: Usa id (UUID) para contratos do Supabase
  // ✅ FIX: Se for localOnly, cria/atualiza no Supabase primeiro
  // ============================================================
  const handleConcluirEntrega = async () => {
    if (!cargaAtual || !user) return;

    if (!window.confirm('Confirmar entrega desta carga?')) return;

    setConcluindo(true);

    try {
      const agora = new Date().toISOString();
      const contratoId = cargaAtual.id;           // UUID do Supabase (ou null se localStorage)
      const numeroContrato = cargaAtual.numero_contrato;  // CTR-... (sempre existe)
      const cargaId = cargaAtual.carga_id;

      console.log('[PainelMotorista] Concluindo entrega:', { 
        contratoId, 
        numeroContrato, 
        cargaId,
        isLocalOnly: cargaAtual.isLocalOnly 
      });

      // ✅ FIX: Se o contrato só existe no localStorage (sem UUID no Supabase)
      // Precisamos criar/atualizar no Supabase usando numero_contrato
      if (cargaAtual.isLocalOnly || !contratoId) {
        console.log('[PainelMotorista] Contrato localStorage detectado. Buscando no Supabase por numero_contrato...');
        
        // Busca o contrato no Supabase pelo numero_contrato
        const { data: contratoSupabase, error: erroBusca } = await supabase
          .from('contratos')
          .select('id, numero_contrato')
          .eq('numero_contrato', numeroContrato)
          .maybeSingle();

        if (erroBusca) {
          console.warn('[PainelMotorista] Erro ao buscar contrato por numero:', erroBusca);
        }

        if (contratoSupabase?.id) {
          // Achou no Supabase! Usa o UUID real
          console.log('[PainelMotorista] Contrato encontrado no Supabase:', contratoSupabase.id);
          
          const { data: updateData, error: erroContrato } = await supabase
            .from('contratos')
            .update({
              status: 'concluido',
              data_fim: agora,
              updated_at: agora
            })
            .eq('id', contratoSupabase.id)
            .select();

          if (erroContrato) {
            console.error('[PainelMotorista] Erro ao atualizar contrato:', erroContrato);
            console.error('[PainelMotorista] Detalhes:', erroContrato.message, erroContrato.code);
            throw erroContrato;
          }

          console.log('[PainelMotorista] Contrato atualizado no Supabase:', updateData);
        } else {
          // Não achou no Supabase — só atualiza localStorage
          console.warn('[PainelMotorista] Contrato não encontrado no Supabase. Atualizando só localStorage.');
        }
      } else {
        // ✅ Contrato tem UUID válido — atualiza normalmente
        if (!contratoId) {
          throw new Error('ID do contrato não encontrado');
        }

        const { data: updateData, error: erroContrato } = await supabase
          .from('contratos')
          .update({
            status: 'concluido',
            data_fim: agora,
            updated_at: agora
          })
          .eq('id', contratoId)
          .select();

        if (erroContrato) {
          console.error('[PainelMotorista] Erro ao atualizar contrato:', erroContrato);
          console.error('[PainelMotorista] Detalhes:', erroContrato.message, erroContrato.code);
          throw erroContrato;
        }

        console.log('[PainelMotorista] Contrato atualizado no Supabase:', updateData);
      }

      // 2️⃣ Atualizar carga no Supabase (se tiver carga_id)
      if (cargaId) {
        const { error: erroCarga } = await supabase
          .from('cargas')
          .update({ 
            status: 'entregue',
            updated_at: agora
          })
          .eq('id', cargaId);

        if (erroCarga) {
          console.warn('[PainelMotorista] Erro ao atualizar carga:', erroCarga);
        } else {
          console.log('[PainelMotorista] Carga atualizada no Supabase');
        }
      }

      // 3️⃣ Atualizar localStorage — contratos (por numero_contrato ou id)
      const contratos = JSON.parse(localStorage.getItem('borafrete_contratos') || '[]');
      const idxContrato = contratos.findIndex(c => 
        c.id === contratoId || 
        c.id === numeroContrato || 
        c.numero_contrato === numeroContrato
      );
      if (idxContrato >= 0) {
        contratos[idxContrato].status = 'concluido';
        contratos[idxContrato].data_fim = agora;
        localStorage.setItem('borafrete_contratos', JSON.stringify(contratos));
        console.log('[PainelMotorista] Contrato atualizado no localStorage');
      }

      // 4️⃣ Atualizar localStorage — cargas
      if (cargaId) {
        const cargas = JSON.parse(localStorage.getItem('borafrete_cargas') || '[]');
        const idxCarga = cargas.findIndex(c => c.id === cargaId);
        if (idxCarga >= 0) {
          cargas[idxCarga].status = 'entregue';
          localStorage.setItem('borafrete_cargas', JSON.stringify(cargas));
        }
      }

      // 5️⃣ Forçar recálculo de nível/selos
      const entregasAtualizadas = (entregas || 0) + 1;

      if (typeof verificarEvolucao === 'function') {
        const evoluiu = verificarEvolucao(entregasAtualizadas);
        if (evoluiu) {
          toast.success(`🎉 Parabéns! Você alcançou o nível: ${nivel?.nome || 'Novo Nível'}!`, {
            duration: 5000
          });
        } else {
          toast.success('✅ Entrega concluída com sucesso! +5 pontos', {
            duration: 3000
          });
        }
      } else {
        toast.success('✅ Entrega concluída com sucesso! +5 pontos');
      }

      // 6️⃣ Resetar estado e recarregar
      setCargaAtual(null);
      dadosCarregados.current = false;

      setTimeout(() => {
        carregarDados(user);
      }, 1500);

    } catch (error) {
      console.error('[PainelMotorista] Erro ao concluir entrega:', error);
      console.error('[PainelMotorista] Stack:', error.stack);
      toast.error('Erro ao concluir entrega. Tente novamente.');
    } finally {
      setConcluindo(false);
    }
  };

  const contarNotificacoesNaoLidas = (userId) => {
    if (!userId) return;
    const notificacoes = JSON.parse(localStorage.getItem('borafrete_notificacoes') || '[]');
    const naoLidas = notificacoes.filter(n => 
      (n.motoristaId === userId || n.destinatario === 'todos') && !n.lida
    ).length;
    setNotificacoesNaoLidas(naoLidas);
  };

  useEffect(() => {
    if (user?.id) {
      contarNotificacoesNaoLidas(user.id);
    }
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-10 h-10 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">

      {/* Header com Logo Borinho */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <img 
                  src="/logoborinho45.png" 
                  alt="BoraFrete" 
                  className="h-10 w-auto object-contain"
                />
              </div>
              <div className="h-6 w-px bg-slate-200 hidden sm:block" />
              <span className="text-slate-600 hidden sm:block font-medium">
                {user?.nome?.split(' ')[0] || 'Motorista'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigate('/notificacoes')} className="relative">
                <Bell className="w-5 h-5 text-slate-600" />
                {notificacoesNaoLidas > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF6B35] text-white text-xs rounded-full flex items-center justify-center font-bold animate-pulse">
                    {notificacoesNaoLidas > 9 ? '9+' : notificacoesNaoLidas}
                  </span>
                )}
              </Button>

              <Button variant="ghost" size="sm" onClick={() => navigate('/perfil')} className="text-slate-600 hover:text-slate-900">
                <User className="w-4 h-4 mr-2" /> Perfil
              </Button>

              <Button variant="ghost" size="sm" onClick={() => navigate('/gerenciadora-risco')} className="text-slate-600 hover:text-slate-900">
                <Shield className="w-4 h-4 mr-2" /> Gerenciadora
              </Button>

              <Button variant="ghost" size="sm" onClick={logout} className="text-slate-600 hover:text-red-600">
                <LogOut className="w-4 h-4 mr-2" /> Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* CARD DE NÍVEL E PONTUAÇÃO */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* CARD PRINCIPAL DE NÍVEL */}
          <Card className="lg:col-span-2 border-0 shadow-xl bg-white overflow-hidden transform hover:scale-[1.01] transition-transform duration-300">
            <div className={`p-8 bg-gradient-to-br ${nivel?.cor || 'from-slate-500 to-slate-600'} text-white relative overflow-hidden`}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

              <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md rounded-full px-4 py-2 flex items-center gap-2 shadow-lg border border-white/10 animate-pulse">
                <Sparkles className="w-5 h-5 text-yellow-300" />
                <span className="font-bold text-lg">{pontuacaoSegura} pts</span>
              </div>

              <div className="relative z-10">
                <div className="mb-4">
                  <p className="text-white/80 text-sm font-medium uppercase tracking-widest mb-2">Nível Atual</p>
                  <h2 className="text-4xl font-black mb-2 flex items-center gap-3 tracking-tight">
                    {nivel?.nome || 'Bronze'}
                    <Medal className="w-10 h-10 text-yellow-300 drop-shadow-lg" />
                  </h2>
                  <p className="text-white/90 text-base font-medium">{nivel?.descricao || 'Início da jornada'}</p>
                </div>

                <div className="flex flex-wrap items-center gap-4 mb-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/10 flex items-center gap-2 flex-shrink-0">
                    <p className="text-3xl font-black leading-none">{entregas || 0}</p>
                    <p className="text-xs text-white/70 font-medium">entregas</p>
                  </div>

                  {nivel?.beneficios && Array.isArray(nivel.beneficios) && nivel.beneficios.length > 0 && (
                    <div className="flex flex-wrap gap-2 flex-1">
                      {nivel.beneficios.map((b, i) => (
                        <span key={i} className="text-xs bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full font-medium border border-white/10 flex items-center gap-1">
                          <Zap className="w-3 h-3" /> {b}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {proximoNivel && (
                <div className="relative z-10 mt-4">
                  <div className="flex justify-between text-sm mb-3 font-medium">
                    <span className="text-white/90">Progresso para {proximoNivel.nome}</span>
                    <span className="font-bold text-lg">{progresso || 0}%</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-4 overflow-hidden backdrop-blur-sm border border-white/10">
                    <div 
                      className="bg-gradient-to-r from-yellow-300 to-yellow-500 h-full rounded-full transition-all duration-1000 ease-out shadow-lg relative"
                      style={{ width: `${progresso || 0}%` }}
                    >
                      <div className="absolute inset-0 bg-white/20 animate-pulse" />
                    </div>
                  </div>
                  <p className="text-sm text-white/80 mt-3 font-medium">
                    Faltam <span className="font-bold text-white text-lg">{entregasRestantes || 0}</span> entregas para o próximo nível
                  </p>
                </div>
              )}

              <div className="relative z-10 mt-6 pt-6 border-t border-white/20 grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold">{historicoSeguro.length}</p>
                  <p className="text-xs text-white/70">Concluídas</p>
                </div>
                <div className="text-center border-x border-white/20">
                  <p className="text-2xl font-bold">{selosSeguros.filter(s => s?.conquistado).length}</p>
                  <p className="text-xs text-white/70">Selos</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-300">
                    R$ {parseFloat(statsSeguros?.valorTotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </p>
                  <p className="text-xs text-white/70">Faturado</p>
                </div>
              </div>
            </div>
          </Card>

          {/* CARD LATERAL DE ESTATÍSTICAS RÁPIDAS */}
          <Card className="border-0 shadow-xl bg-white overflow-hidden">
            <CardHeader className="pb-3 bg-slate-900 text-white">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Target className="w-5 h-5 text-yellow-400" /> Estatísticas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-6">
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">Valor Total</span>
                </div>
                <span className="font-bold text-slate-900 text-lg">
                  R$ {parseFloat(statsSeguros?.valorTotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>

              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Flame className="w-5 h-5 text-orange-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">Este Mês</span>
                </div>
                <span className="text-2xl font-bold text-slate-900">
                  {historicoSeguro.filter(c => {
                    const data = c?.data_conclusao ? new Date(c.data_conclusao) : null;
                    const hoje = new Date();
                    return data && data.getMonth() === hoje.getMonth() && data.getFullYear() === hoje.getFullYear();
                  }).length}
                </span>
              </div>

              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl border border-purple-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Award className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">Selos</span>
                </div>
                <span className="font-bold text-slate-900 text-lg">
                  {selosSeguros.filter(s => s?.conquistado).length}/{selosSeguros.length || selosBaseSeguros.length}
                </span>
              </div>

              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Trophy className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">Pontuação</span>
                </div>
                <span className="font-bold text-amber-600 text-lg">
                  {pontuacaoSegura} pts
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* SELOS DE CONQUISTA */}
        <Card className="mb-8 border-0 shadow-xl bg-white overflow-hidden">
          <CardHeader className="bg-slate-900 text-white pb-4">
            <CardTitle className="flex items-center gap-3 text-xl">
              <Trophy className="w-6 h-6 text-yellow-400" /> 
              Selos de Conquista
            </CardTitle>
            <CardDescription className="text-slate-400">
              Complete missões para ganhar selos exclusivos e pontos de experiência
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {selosSeguros.length > 0 ? selosSeguros.map((selo) => {
                const Icone = ICONES_SELO[selo?.icon] || Award;
                return (
                  <div 
                    key={selo?.id || Math.random()}
                    className={`relative p-5 rounded-2xl border-2 transition-all duration-300 cursor-pointer ${
                      selo?.conquistado 
                        ? `${selo?.cor || 'bg-slate-500'} border-transparent text-white shadow-lg transform hover:scale-105 hover:shadow-xl` 
                        : 'bg-slate-50 border-slate-200 text-slate-400 hover:border-slate-300 hover:bg-slate-100'
                    }`}
                  >
                    <div className="flex flex-col items-center text-center gap-3">
                      <div className={`p-4 rounded-xl ${selo?.conquistado ? 'bg-white/20 backdrop-blur-sm' : 'bg-slate-200'}`}>
                        <Icone className={`w-8 h-8 ${selo?.conquistado ? 'text-white' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <p className={`font-bold text-base ${selo?.conquistado ? 'text-white' : 'text-slate-600'}`}>
                          {selo?.nome || 'Selo'}
                        </p>
                        <p className={`text-xs mt-1 leading-relaxed ${selo?.conquistado ? 'text-white/80' : 'text-slate-400'}`}>
                          {selo?.descricao || ''}
                        </p>
                      </div>
                      {selo?.conquistado && (
                        <Badge className="bg-white/30 text-white border-0 text-xs font-bold backdrop-blur-sm">
                          +{selo?.pontos || 0} XP
                        </Badge>
                      )}
                      {!selo?.conquistado && (
                        <Badge className="bg-slate-200 text-slate-500 border-0 text-xs">
                          +{selo?.pontos || 0} XP
                        </Badge>
                      )}
                      {selo?.conquistado && (
                        <div className="absolute -top-2 -right-2 w-7 h-7 bg-green-500 rounded-full flex items-center justify-center shadow-lg animate-bounce">
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              }) : (
                selosBaseSeguros.map((seloBase) => {
                  const Icone = ICONES_SELO[seloBase?.icon] || Award;
                  return (
                    <div 
                      key={seloBase?.id || Math.random()}
                      className="relative p-5 rounded-2xl border-2 bg-slate-50 border-slate-200 text-slate-400 hover:border-slate-300 hover:bg-slate-100 transition-all duration-300 cursor-pointer"
                    >
                      <div className="flex flex-col items-center text-center gap-3">
                        <div className="p-4 rounded-xl bg-slate-200">
                          <Icone className="w-8 h-8 text-slate-400" />
                        </div>
                        <div>
                          <p className="font-bold text-base text-slate-600">{seloBase?.nome || 'Selo'}</p>
                          <p className="text-xs mt-1 leading-relaxed text-slate-400">{seloBase?.descricao || ''}</p>
                        </div>
                        <Badge className="bg-slate-200 text-slate-500 border-0 text-xs">
                          +{seloBase?.pontos || 0} XP
                        </Badge>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>

        {/* TABS */}
        <Tabs value={abaAtiva} onValueChange={setAbaAtiva} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-xl w-full md:w-auto shadow-sm">
            <TabsTrigger value="atual" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-lg px-6 py-2.5 font-semibold transition-all">
              <Truck className="w-4 h-4 mr-2" />
              Carga Atual
              {cargaAtual && <span className="ml-2 w-2 h-2 bg-green-500 rounded-full animate-pulse" />}
            </TabsTrigger>
            <TabsTrigger value="historico" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-lg px-6 py-2.5 font-semibold transition-all">
              <Package className="w-4 h-4 mr-2" />
              Histórico
              <Badge variant="secondary" className="ml-2 bg-slate-100 text-slate-600 font-bold">
                {historicoSeguro.length}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="niveis" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-lg px-6 py-2.5 font-semibold transition-all">
              <Award className="w-4 h-4 mr-2" />
              Níveis
            </TabsTrigger>
            <TabsTrigger value="estatisticas" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white rounded-lg px-6 py-2.5 font-semibold transition-all">
              <TrendingUp className="w-4 h-4 mr-2" />
              Desempenho
            </TabsTrigger>
          </TabsList>

          {/* ABA: CARGA ATUAL */}
          <TabsContent value="atual" className="mt-0">
            {cargaAtual ? (
              <Card className="border-0 shadow-xl overflow-hidden">
                <div className="bg-slate-900 text-white p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-white/10 rounded-xl">
                        <Truck className="w-7 h-7" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold">Carga em Andamento</h3>
                        <p className="text-sm text-slate-400">
                          #{cargaAtual.numero_contrato?.slice(-8) || cargaAtual.id?.slice(-8)} • Iniciada em{' '}
                          {cargaAtual.data_inicio ? new Date(cargaAtual.data_inicio).toLocaleDateString('pt-BR') : '-'}
                        </p>
                      </div>
                    </div>
                    <Badge className="bg-green-500 text-white border-0 px-4 py-1.5 font-bold">
                      <Clock className="w-4 h-4 mr-1" /> EM ROTA
                    </Badge>
                  </div>
                </div>

                <CardContent className="p-6 space-y-6">
                  {/* Progresso */}
                  <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-semibold text-slate-700">Progresso da Entrega</span>
                      <span className="text-2xl font-black text-slate-900">{cargaAtual.progresso || 0}%</span>
                    </div>
                    <Progress value={cargaAtual.progresso || 0} className="h-3" />
                    <p className="text-xs text-slate-500 mt-2 font-medium">
                      Prazo final: {cargaAtual.data_fim ? new Date(cargaAtual.data_fim).toLocaleDateString('pt-BR') : '-'}
                    </p>
                  </div>

                  {/* Origem e Destino */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200 hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-2 mb-3">
                        <Navigation className="w-5 h-5 text-blue-600" />
                        <span className="text-xs font-bold text-blue-600 uppercase tracking-wide">Origem</span>
                      </div>
                      <h4 className="text-xl font-bold text-slate-900">{cargaAtual.origem_cidade}</h4>
                      <p className="text-sm text-slate-600 font-medium">{cargaAtual.origem_estado}</p>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200 hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-2 mb-3">
                        <MapPin className="w-5 h-5 text-green-600" />
                        <span className="text-xs font-bold text-green-600 uppercase tracking-wide">Destino</span>
                      </div>
                      <h4 className="text-xl font-bold text-slate-900">{cargaAtual.destino_cidade}</h4>
                      <p className="text-sm text-slate-600 font-medium">{cargaAtual.destino_estado}</p>
                    </div>
                  </div>

                  {/* Grid de info */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-5 text-center border border-amber-200 hover:shadow-md transition-shadow">
                      <DollarSign className="w-6 h-6 text-amber-600 mx-auto mb-2" />
                      <p className="text-xs text-slate-500 mb-1 font-medium">Valor Frete</p>
                      <p className="font-bold text-slate-900 text-lg">
                        R$ {parseFloat(cargaAtual.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-5 text-center border border-slate-200 hover:shadow-md transition-shadow">
                      <Weight className="w-6 h-6 text-slate-600 mx-auto mb-2" />
                      <p className="text-xs text-slate-500 mb-1 font-medium">Peso</p>
                      <p className="font-bold text-slate-900 text-lg">{cargaAtual.peso || '-'} kg</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-5 text-center border border-purple-200 hover:shadow-md transition-shadow">
                      <Truck className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                      <p className="text-xs text-slate-500 mb-1 font-medium">Veículo</p>
                      <p className="font-bold text-slate-900 text-lg">{cargaAtual.tipo_veiculo || '-'}</p>
                    </div>
                  </div>

                  {/* Empresa */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
                    <p className="text-xs text-blue-700 uppercase tracking-wide font-bold mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Empresa Contratante
                    </p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-slate-900 text-lg">{cargaAtual.empresa_nome || 'Empresa'}</p>
                        {cargaAtual.empresa_telefone && (
                          <p className="text-sm text-slate-600 font-medium">{cargaAtual.empresa_telefone}</p>
                        )}
                      </div>
                      {cargaAtual.empresa_telefone && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`https://wa.me/${cargaAtual.empresa_telefone.replace(/\D/g, '')}`, '_blank')}
                          className="border-green-600 text-green-600 hover:bg-green-50 font-semibold"
                        >
                          <Phone className="w-4 h-4 mr-1" /> WhatsApp
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Botões */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-200">
                    <Button
                      onClick={handleConcluirEntrega}
                      disabled={concluindo}
                      className="flex-1 h-14 bg-green-600 hover:bg-green-700 text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all"
                    >
                      {concluindo ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processando...</>
                      ) : (
                        <><CheckCircle className="w-5 h-5 mr-2" /> Concluir Entrega (+5 pts)</>
                      )}
                    </Button>

                    <Button
                      variant="outline"
                      onClick={() => navigate(`/carga/${cargaAtual.carga_id}`)}
                      className="flex-1 h-14 font-semibold border-2"
                    >
                      <FileText className="w-5 h-5 mr-2" /> Ver Detalhes Completos
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-slate-200">
                <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Truck className="w-10 h-10 text-slate-400" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">Nenhuma carga em andamento</h3>
                <p className="text-slate-600 mb-8 text-lg">Você não possui cargas ativas no momento.</p>

                <Button onClick={() => navigate('/buscar-cargas')} className="h-14 px-8 bg-slate-900 hover:bg-slate-800 text-lg font-bold">
                  <Search className="w-5 h-5 mr-2" /> Buscar Cargas Disponíveis
                </Button>
              </div>
            )}
          </TabsContent>

          {/* ABA: HISTÓRICO */}
          <TabsContent value="historico" className="mt-0">
            {historicoSeguro.length > 0 ? (
              <div className="space-y-4">
                {historicoSeguro.map((carga) => (
                  <Card key={carga?.id || Math.random()} className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer hover:border-slate-200">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-green-100 rounded-xl">
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-bold text-slate-900 text-lg">{carga?.origem_cidade || '-'}/{carga?.origem_estado || '-'}</span>
                              <ArrowRight className="w-5 h-5 text-slate-400" />
                              <span className="font-bold text-slate-900 text-lg">{carga?.destino_cidade || '-'}/{carga?.destino_estado || '-'}</span>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-slate-500">
                              <span className="font-medium">{carga?.titulo || 'Carga'}</span>
                              <span>•</span>
                              <span>{carga?.data_conclusao ? new Date(carga.data_conclusao).toLocaleDateString('pt-BR') : '-'}</span>
                              <span>•</span>
                              <span className="font-bold text-slate-700 text-base">
                                R$ {parseFloat(carga?.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                            {carga?.empresa_nome && (
                              <p className="text-xs text-slate-400 mt-1 font-medium">{carga.empresa_nome}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge variant="secondary" className="bg-green-100 text-green-700 border-0 font-bold px-3 py-1">
                            <CheckCircle className="w-3 h-3 mr-1" /> Concluída
                          </Badge>
                          <span className="text-xs text-slate-400 font-medium">+5 pts</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
                <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 text-lg font-medium">Nenhuma carga concluída ainda</p>
                <p className="text-sm text-slate-400 mt-2">Complete sua primeira entrega para começar!</p>
              </div>
            )}
          </TabsContent>

          {/* ABA: NÍVEIS */}
          <TabsContent value="niveis" className="mt-0">
            <div className="space-y-4">
              {niveisSeguros.map((nivelItem) => {
                const alcancado = (entregas || 0) >= (nivelItem?.entregas || 0);
                const isAtual = nivel?.id === nivelItem?.id;

                return (
                  <div 
                    key={nivelItem?.id || Math.random()} 
                    className={`flex items-center gap-5 p-6 rounded-2xl border-2 transition-all duration-300 ${
                      isAtual 
                        ? 'bg-white border-slate-900 shadow-xl transform hover:scale-[1.01]' 
                        : alcancado 
                          ? 'bg-slate-50 border-slate-200 hover:border-slate-300' 
                          : 'bg-white border-slate-100 opacity-50'
                    }`}
                  >
                    <div className={`p-4 rounded-xl ${
                      isAtual ? 'bg-slate-900 shadow-lg' : alcancado ? 'bg-slate-200' : 'bg-slate-100'
                    }`}>
                      <Award className={`w-8 h-8 ${
                        isAtual ? 'text-yellow-400' : alcancado ? 'text-slate-600' : 'text-slate-400'
                      }`} />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <p className={`font-black text-xl ${
                          isAtual ? 'text-slate-900' : alcancado ? 'text-slate-700' : 'text-slate-500'
                        }`}>
                          {nivelItem?.nome || 'Nível'}
                        </p>
                        {isAtual && (
                          <Badge className="bg-slate-900 text-white border-0 px-3 py-1 font-bold">
                            <Star className="w-3 h-3 mr-1" /> ATUAL
                          </Badge>
                        )}
                        {alcancado && !isAtual && (
                          <Badge variant="secondary" className="bg-green-100 text-green-700 border-0 font-bold">
                            <CheckCircle className="w-3 h-3 mr-1" /> Alcançado
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 font-medium">{(nivelItem?.entregas || 0)} entregas necessárias • {nivelItem?.descricao || ''}</p>

                      {nivelItem?.beneficios && Array.isArray(nivelItem.beneficios) && nivelItem.beneficios.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {nivelItem.beneficios.map((b, i) => (
                            <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium ${
                              alcancado ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-slate-100 text-slate-400'
                            }`}>
                              {alcancado ? '✓' : '○'} {b}
                            </span>
                          ))}
                        </div>
                      )}

                      {nivelItem?.sorteio && Array.isArray(nivelItem.sorteio) && nivelItem.sorteio.length > 0 && (
                        <div className="mt-3 p-3 bg-amber-50 rounded-xl border border-amber-200">
                          <p className="text-xs text-amber-700 font-bold flex items-center gap-2">
                            <Gift className="w-4 h-4" /> 🎁 Sorteio: {nivelItem.sorteio.join(', ')}
                          </p>
                        </div>
                      )}
                    </div>

                    {alcancado && <CheckCircle className="w-8 h-8 text-green-600" />}
                  </div>
                );
              })}
            </div>
          </TabsContent>

          {/* ABA: ESTATÍSTICAS */}
          <TabsContent value="estatisticas" className="mt-0">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-0 shadow-xl">
                <CardHeader className="pb-3 bg-slate-900 text-white">
                  <CardTitle className="text-base font-bold flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-yellow-400" /> Entregas Realizadas
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-5 bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl border border-slate-200">
                      <span className="text-slate-700 font-bold">Total de Entregas</span>
                      <span className="text-4xl font-black text-slate-900">{entregas || 0}</span>
                    </div>
                    <div className="flex items-center justify-between p-5 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-200">
                      <span className="text-slate-700 font-bold">Este Mês</span>
                      <span className="text-2xl font-bold text-slate-900">
                        {historicoSeguro.filter(c => {
                          const data = c?.data_conclusao ? new Date(c.data_conclusao) : null;
                          const hoje = new Date();
                          return data && data.getMonth() === hoje.getMonth() && data.getFullYear() === hoje.getFullYear();
                        }).length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-5 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200">
                      <span className="text-slate-700 font-bold">Pontuação Total</span>
                      <span className="text-2xl font-bold text-amber-600">{pontuacaoSegura} pts</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl">
                <CardHeader className="pb-3 bg-slate-900 text-white">
                  <CardTitle className="text-base font-bold flex items-center gap-2">
                    <Award className="w-5 h-5 text-yellow-400" /> Próximo Nível
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  {proximoNivel ? (
                    <div className="space-y-5">
                      <div className="flex items-center gap-4">
                        <div className={`p-4 rounded-xl bg-gradient-to-r ${proximoNivel?.cor || 'from-slate-500 to-slate-600'} shadow-lg`}>
                          <Award className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <p className="font-black text-slate-900 text-xl">{proximoNivel?.nome || 'Próximo'}</p>
                          <p className="text-sm text-slate-500 font-medium">{proximoNivel?.descricao || ''}</p>
                        </div>
                      </div>
                      <div className="pt-2">
                        <div className="flex justify-between text-sm mb-2 font-bold">
                          <span className="text-slate-700">Progresso</span>
                          <span className="text-slate-900">{progresso || 0}%</span>
                        </div>
                        <Progress value={progresso || 0} className="h-3" />
                        <p className="text-sm text-slate-500 mt-3 font-medium">
                          Faltam <span className="font-bold text-slate-900 text-lg">{entregasRestantes || 0}</span> entregas
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Crown className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                      <p className="text-slate-700 font-bold text-xl">Você atingiu o nível máximo!</p>
                      <p className="text-sm text-slate-500 mt-2">Black Diamante - Você é LENDÁRIO! 🏆</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Evolução dos Níveis */}
            <Card className="mt-6 border-0 shadow-xl">
              <CardHeader className="bg-slate-900 text-white">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="w-5 h-5 text-yellow-400" /> Evolução dos Níveis
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Programa de fidelidade BoraFrete - Complete entregas para subir de nível
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3">
                  {niveisSeguros.map((nivelItem) => {
                    const alcancado = (entregas || 0) >= (nivelItem?.entregas || 0);
                    return (
                      <div key={nivelItem?.id || Math.random()} className={`flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${alcancado ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-100 opacity-50'}`}>
                        <div className={`p-3 rounded-lg ${alcancado ? 'bg-slate-900 shadow-md' : 'bg-slate-200'}`}>
                          <Award className={`w-6 h-6 ${alcancado ? 'text-yellow-400' : 'text-slate-400'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className={`font-bold text-lg ${alcancado ? 'text-slate-900' : 'text-slate-500'}`}>{nivelItem?.nome || 'Nível'}</p>
                            {alcancado && <Badge variant="secondary" className="bg-green-100 text-green-700 border-0 font-bold text-xs">Alcançado</Badge>}
                          </div>
                          <p className="text-sm text-slate-500 font-medium">{nivelItem?.entregas || 0} entregas • {nivelItem?.descricao || ''}</p>
                        </div>
                        {alcancado && <CheckCircle className="w-6 h-6 text-green-600" />}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Ações Rápidas */}
        <div className="grid md:grid-cols-2 gap-4 mt-8">
          <button onClick={() => navigate('/buscar-cargas')} className="flex items-center gap-4 p-6 bg-slate-900 text-white rounded-2xl hover:bg-slate-800 transition-all text-left group shadow-lg hover:shadow-xl">
            <div className="p-3 bg-white/10 rounded-xl group-hover:bg-white/20 transition-colors">
              <Search className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-lg">Buscar Cargas</p>
              <p className="text-sm text-slate-400">Encontre novos fretes disponíveis</p>
            </div>
            <ChevronRight className="w-6 h-6 text-slate-400 group-hover:text-white transition-colors" />
          </button>

          <button onClick={() => navigate('/perfil')} className="flex items-center gap-4 p-6 bg-white border-2 border-slate-200 rounded-2xl hover:border-slate-300 hover:shadow-xl transition-all text-left group">
            <div className="p-3 bg-slate-100 rounded-xl group-hover:bg-slate-200 transition-colors">
              <User className="w-7 h-7 text-slate-600" />
            </div>
            <div className="flex-1">
              <p className="font-bold text-lg text-slate-900">Meu Perfil</p>
              <p className="text-sm text-slate-500">Ver e editar informações</p>
            </div>
            <ChevronRight className="w-6 h-6 text-slate-400 group-hover:text-slate-600 transition-colors" />
          </button>
        </div>

      </main>
    </div>
  );
};

export default PainelMotorista;